import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { checkDnsWizard, dnsChangeHistory } from "@/lib/dns-wizard.functions";
import {
  APEX_HOST,
  WWW_HOST,
  RECOMMENDED_RECORDS,
  type RecommendedRecord,
} from "@/lib/dns-records";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/dns-wizard")({
  component: DnsWizard,
  head: () => ({
    meta: [
      { title: "DNS & SSL Wizard — Vending Machine Free" },
      { name: "description", content: "Internal wizard for configuring and verifying the www hostname." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Mode = "a" | "cname";

function DnsWizard() {
  const [mode, setMode] = useState<Mode>("a");
  const run = useServerFn(checkDnsWizard);
  const history = useServerFn(dnsChangeHistory);
  const check = useQuery({ queryKey: ["dns-wizard"], queryFn: () => run() });
  const log = useQuery({ queryKey: ["dns-history"], queryFn: () => history() });

  const records = RECOMMENDED_RECORDS.filter((r) => r.id === mode || r.id === "txt");
  const data = check.data;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-10 space-y-6">
      <header>
        <h1 className="text-3xl font-bold">DNS &amp; SSL wizard — {WWW_HOST}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Produces the exact records to add at your registrar, then verifies DNS, certificate and
          robots.txt before you publish.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Step 1 — Choose your DNS setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-2">
            <Button variant={mode === "a" ? "default" : "outline"} size="sm" onClick={() => setMode("a")}>
              Standard DNS (A record)
            </Button>
            <Button
              variant={mode === "cname" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("cname")}
            >
              Cloudflare / proxied (CNAME)
            </Button>
          </div>
          <p className="text-muted-foreground">
            {mode === "a"
              ? "Most registrars: point www straight at the hosting IP."
              : "Proxied DNS can’t use the A record path — connect the domain with the “Domain uses Cloudflare or a similar proxy” box ticked and use a CNAME."}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Step 2 — Add these records at your registrar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {records.map((r) => (
            <RecordRow key={r.id} record={r} />
          ))}
          <p className="text-muted-foreground text-xs">
            Then in Project settings → Domains → Connect Domain, enter <code>{WWW_HOST}</code> in
            full (www is never added automatically) and keep <code>{APEX_HOST}</code> as Primary so
            www 301-redirects to it.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>Step 3 — Verify DNS, certificate &amp; ciphers</span>
            <Button size="sm" variant="outline" onClick={() => check.refetch()} disabled={check.isFetching}>
              {check.isFetching ? "Checking…" : "Re-run checks"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {check.isLoading ? (
            <p className="text-muted-foreground">Resolving DNS and probing TLS…</p>
          ) : !data ? (
            <p className="text-muted-foreground">Check failed to run.</p>
          ) : (
            <>
              <ul className="space-y-2">
                {data.steps.map((s) => (
                  <li key={s.id} className="flex gap-2">
                    <span
                      className={
                        s.state === "pass"
                          ? "text-green-600"
                          : s.state === "fail"
                            ? "text-red-600"
                            : "text-amber-600"
                      }
                    >
                      {s.state === "pass" ? "✓" : s.state === "fail" ? "✗" : "…"}
                    </span>
                    <span>
                      <span className="font-medium">{s.label}</span>
                      <br />
                      <span className="text-muted-foreground text-xs">{s.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="rounded-md border p-3 text-xs space-y-1">
                <p className="font-medium">TLS detail</p>
                <p className="text-muted-foreground">
                  Handshake: {data.tls.handshakeOk ? "succeeded — trusted certificate and a mutually supported cipher suite (TLS 1.2/1.3)" : "failed"}
                  {data.tls.cipherMismatch && " · protocol/cipher mismatch"}
                </p>
                <p className="text-muted-foreground">
                  HSTS: {data.tls.hsts ?? "not set"} · Response: {data.tls.status ?? "—"}
                  {data.tls.redirectTo ? ` → ${data.tls.redirectTo}` : ""}
                </p>
                <p className="text-muted-foreground">Checked {new Date(data.checkedAt).toLocaleString()}</p>
              </div>
              <p
                className={
                  data.readyToPublish ? "text-green-600 font-medium" : "text-amber-600 font-medium"
                }
              >
                {data.readyToPublish ? "✓ " : "⚠ "}
                {data.nextAction}
              </p>
              {!data.readyToPublish && (
                <p className="text-muted-foreground text-xs">
                  Publishing before these pass leaves Google reporting “Robots.txt unreachable” for
                  www URLs. DNS changes can take up to a few hours to propagate.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>Step 4 — Change history &amp; indexing impact</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => log.refetch()}
              disabled={log.isFetching}
            >
              {log.isFetching ? "Loading…" : "Refresh log"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {log.isLoading ? (
            <p className="text-muted-foreground">Loading change history…</p>
          ) : !log.data || log.data.length === 0 ? (
            <p className="text-muted-foreground">
              No changes recorded yet. Every time this page runs its checks, the observed DNS, TLS
              and redirect state is fingerprinted — a new entry appears whenever any value changes.
            </p>
          ) : (
            <ul className="space-y-3">
              {log.data.map((e) => (
                <li key={e.id} className="rounded-md border p-3 space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{new Date(e.observedAt).toLocaleString()}</span>
                    <span className="text-muted-foreground text-xs">
                      {e.host} ·{" "}
                      {e.mode === "A"
                        ? "registrar A record"
                        : e.mode === "CNAME"
                          ? "proxied CNAME (Cloudflare-style)"
                          : "no record published"}
                    </span>
                  </div>
                  <p>{e.changeSummary}</p>
                  <p className="text-muted-foreground text-xs">
                    A=[{e.aRecords.join(", ") || "none"}] · CNAME=[{e.cnameRecords.join(", ") || "none"}] ·
                    TXT {e.txtVerified ? "verified" : "missing"} · TLS {e.tlsOk ? "valid" : e.cipherMismatch ? "cipher mismatch" : "failing"} ·
                    redirect {e.redirectsToApex ? "ok" : "no"} · robots {e.robotsOk ? "ok" : "no"}
                  </p>
                  <p
                    className={
                      e.impact.verdict === "improved"
                        ? "text-green-600 text-xs"
                        : e.impact.verdict === "worsened"
                          ? "text-red-600 text-xs"
                          : "text-muted-foreground text-xs"
                    }
                  >
                    {e.impact.verdict === "pending"
                      ? "Indexing impact: waiting on the next Search Console index check after this change."
                      : `Indexing impact: failure rate ${(e.impact.before!.failureRate * 100).toFixed(0)}% → ${(e.impact.after!.failureRate * 100).toFixed(0)}% (${e.impact.delta! > 0 ? "+" : ""}${(e.impact.delta! * 100).toFixed(0)} pts, ${e.impact.verdict.replace("-", " ")})`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RecordRow({ record }: { record: RecommendedRecord }) {
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    toast.success("Copied");
  };
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="grid sm:grid-cols-4 gap-2 text-sm">
        <Field label="Type" value={record.type} />
        <Field label="Name / Host" value={record.name} onCopy={() => copy(record.name)} />
        <Field label="Value" value={record.value} onCopy={() => copy(record.value)} />
        <Field label="TTL" value={record.ttl} />
      </div>
      <p className="text-muted-foreground text-xs">{record.when}</p>
    </div>
  );
}

function Field({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-[11px] uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-1">
        <code className="truncate text-xs">{value}</code>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="text-primary text-[11px] hover:underline shrink-0"
            aria-label={`Copy ${label}`}
          >
            copy
          </button>
        )}
      </div>
    </div>
  );
}
